import { Box, IconButton } from "@mui/material";
import React, { useContext, useState } from "react";
import { Link } from "react-router-dom";
import { formatString } from "../../../common";
import ConfirmDialog from "../../../common/components/ConfirmDialog";
import IconComponent from "../../../common/components/IconComponent";
import { IExpressionEvaluatorContext } from "../../../ExpressionsModule/Models/IExpressionEvaluatorContext";
import { useErrorHandler } from "../../../common/hooks";
import { VariableNames } from "../../Constants/VariableNames";
import BundleContext from "../../Contexts/BundleContext";
import { useComponentSelection } from "../../Hooks/useComponentSelection";
import useComponentVisibility from "../../Hooks/useComponentVisibility";
import useObservedExpression from "../../Hooks/useObservedExpression";
import { TableColumnActionModel } from "../../Models/TableColumnActionModel";
import { IsDesignMode } from "../../Utils/runtimeDetection";
import ViewComponent from "../View/ViewComponent";

const PANEL_HEADER_ACTIONS_PATH_SEGMENT = "headerActions";
const PANEL_HEADER_ACTION_TYPE = "TableColumnAction";

type PanelActionProps = {
  actions: TableColumnActionModel[];
  size?: "small" | "medium" | "large";
  formatData?: any;
  context?: IExpressionEvaluatorContext;
  ownerComponentId?: string;
  ownerComponentPath?: string[];
  actionPathSegment?: string;
};

type PanelActionItemProps = {
  action: TableColumnActionModel;
  index: number;
  size: "small" | "medium" | "large";
  formatData?: any;
  context?: IExpressionEvaluatorContext;
  isExecuting: boolean;
  isActionExecuting: boolean;
  ownerComponentId?: string;
  ownerComponentPath?: string[];
  actionPathSegment: string;
  onActionClick: (action: TableColumnActionModel, index: number) => void;
};

const PanelActionItem: React.FC<PanelActionItemProps> = ({
  action,
  index,
  size,
  formatData,
  context,
  isExecuting,
  isActionExecuting,
  ownerComponentId,
  ownerComponentPath,
  actionPathSegment,
  onActionClick,
}) => {
  const { isVisible, hiddenSx } = useComponentVisibility(action, context!, `PanelAction-${index}`);
  const [disabled] = useObservedExpression<boolean | undefined>(action.Disabled, context!, `PanelAction-${index}`);
  const selectionProps = useComponentSelection({
    component: {
      Id: `${ownerComponentId ?? "panel"}-header-action-${index}`,
      Type: PANEL_HEADER_ACTION_TYPE,
      Layout: {},
    },
    componentPath: ownerComponentPath ? [...ownerComponentPath, actionPathSegment, String(index)] : [],
    isContainer: false,
  });
  const selectableProps = ownerComponentPath ? selectionProps : {};

  if (!isVisible) return null;

  const isDisabled = !IsDesignMode && (isExecuting || disabled === true);

  const path = action.Path && formatData ? formatString(action.Path, formatData) : action.Path;
  const hasActionHandler = !!action.OnSelected || !!action.View?.Id;
  const shouldRenderLink = path && !hasActionHandler;

  const button = (
    <IconButton
      title={action.Label}
      size={size}
      onClick={hasActionHandler ? () => onActionClick(action, index) : undefined}
      disabled={isDisabled}
      loading={isActionExecuting}
      sx={hiddenSx}
    >
      <IconComponent iconName={action.IconName} size={size} />
    </IconButton>
  );

  if (shouldRenderLink) {
    return (
      <Box {...selectableProps} component="span" sx={{ display: "inline-flex" }}>
        <Link to={path!} style={{ textDecoration: "none" }}>
          {button}
        </Link>
      </Box>
    );
  }

  return (
    <Box {...selectableProps} component="span" sx={{ display: "inline-flex" }}>
      {button}
    </Box>
  );
};

const PanelAction: React.FC<PanelActionProps> = ({
  actions,
  size = "medium",
  formatData,
  context,
  ownerComponentId,
  ownerComponentPath,
  actionPathSegment = PANEL_HEADER_ACTIONS_PATH_SEGMENT,
}) => {
  const bundleContext = useContext(BundleContext);
  const errorHandler = useErrorHandler();
  const [openDialogIndex, setOpenDialogIndex] = useState<number | null>(null);
  const [openViewIndex, setOpenViewIndex] = useState<number | null>(null);
  const [viewInput, setViewInput] = useState<Record<string, any> | undefined>(undefined);
  const [executingActionIndex, setExecutingActionIndex] = useState<number | null>(null);
  const isExecuting = executingActionIndex !== null;

  const handleActionClick = async (action: TableColumnActionModel, index: number) => {
    if (!context && !action.View?.Id) {
      return;
    }

    if (action.ConfirmationDialog) {
      setOpenDialogIndex(index);
    } else {
      await executeAction(action, index);
    }
  };

  const executeAction = async (action: TableColumnActionModel, index: number) => {
    setExecutingActionIndex(index);
    try {
      const eventContext = context?.CreateChild();
      if (eventContext && formatData !== undefined && formatData !== null) {
        eventContext.VariableScope.set(VariableNames.Item, formatData);
        eventContext.VariableScope.set(VariableNames.Event, formatData);
      }

      if (action.OnSelected && eventContext) {
        await eventContext.Evaluator.evaluate(action.OnSelected, eventContext);
      }

      if (action.View?.Id) {
        const evaluatedInput = action.ViewInput && eventContext ? await eventContext.Evaluator.evaluate(action.ViewInput, eventContext) : {};
        setViewInput(evaluatedInput);
        setOpenViewIndex(index);
      }
    } catch (error) {
      errorHandler(error);
    } finally {
      setExecutingActionIndex(null);
    }
  };

  const handleConfirm = async (action: TableColumnActionModel, index: number) => {
    await executeAction(action, index);
    setOpenDialogIndex(null);
  };

  const handleCancel = () => {
    setOpenDialogIndex(null);
  };

  const handleViewOutput = (actionIndex: number) => async (output: Record<string, any>) => {
    const action = actions[actionIndex];
    if (!action) {
      return;
    }

    if (action.OnViewReturned && context) {
      setExecutingActionIndex(actionIndex);
      try {
        const eventContext = context.CreateChild();
        if (formatData !== undefined && formatData !== null) {
          eventContext.VariableScope.set(VariableNames.Item, formatData);
          eventContext.VariableScope.set(VariableNames.Event, formatData);
        }
        eventContext.VariableScope.set(VariableNames.Output, output);
        await eventContext.Evaluator.evaluate(action.OnViewReturned, eventContext);
      } catch (error) {
        errorHandler(error);
      } finally {
        setExecutingActionIndex(null);
      }
    }

    setOpenViewIndex(null);
    setViewInput(undefined);
  };

  return (
    <>
      {actions.map((action, index) => (
        <PanelActionItem
          key={index}
          action={action}
          index={index}
          size={size}
          formatData={formatData}
          context={context}
          isExecuting={isExecuting}
          isActionExecuting={executingActionIndex === index}
          ownerComponentId={ownerComponentId}
          ownerComponentPath={ownerComponentPath}
          actionPathSegment={actionPathSegment}
          onActionClick={handleActionClick}
        />
      ))}

      {actions.map((action, index) => {
        if (action.ConfirmationDialog && openDialogIndex === index) {
          return (
            <ConfirmDialog
              key={`dialog-${index}`}
              open={true}
              title={action.ConfirmationDialog.Title}
              message={action.ConfirmationDialog.Message}
              onConfirm={() => handleConfirm(action, index)}
              onCancel={handleCancel}
              isLoading={isExecuting}
            />
          );
        }
        return null;
      })}

      {actions.map((action, index) => {
        if (!action.View?.Id || openViewIndex !== index) {
          return null;
        }

        const view = bundleContext.ViewById[action.View.Id];
        if (!view) {
          return null;
        }

        return (
          <ViewComponent
            key={`${action.View.Id}-${index}`}
            component={view}
            open={true}
            input={viewInput}
            setOutput={handleViewOutput(index)}
          />
        );
      })}
    </>
  );
};

export default PanelAction;
